import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, Field, Input } from '@w7/ui'
import { clearAllAuth } from '@/auth/oidc'
import { bookmakerApi, bmErrorMessage } from '@/api/bookmaker'
import {
  clearLabSession,
  getLabAccessToken,
  getLabUserId,
  getLabUsername,
  LAB_2FA_KEY,
} from '@/config/lab'
import { useAppToast } from '@/ui/toaster'
import { BmPage } from './BmPage'

type SetupPhase = 'setup' | 'active' | 'confirm-disable'

function unwrapLab(result: unknown): { value?: boolean; msg?: string; data?: unknown } {
  const row = (result && typeof result === 'object' ? result : {}) as Record<string, unknown>
  if ('value' in row || 'data' in row || 'msg' in row) {
    return row as { value?: boolean; msg?: string; data?: unknown }
  }
  if (row.data && typeof row.data === 'object') {
    return row.data as { value?: boolean; msg?: string; data?: unknown }
  }
  return {}
}

function imageFromSecret(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const o = data as Record<string, unknown>
  if (typeof o.image === 'string') return o.image
  if (typeof o.authImageUrl === 'string') return o.authImageUrl
  return ''
}

/**
 * Lab `/googleAuth` — scan QR, verify OTP, enable/disable Google Authenticator.
 */
export function GoogleAuthPage() {
  const navigate = useNavigate()
  const toast = useAppToast()
  const userName = getLabUsername()
  const userId = getLabUserId()

  const [phase, setPhase] = useState<SetupPhase>('setup')
  const [authImage, setAuthImage] = useState('')
  const [otp, setOtp] = useState('')
  const [otpOk, setOtpOk] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshFlag = useCallback(async () => {
    if (!userName) return false
    const result = await bookmakerApi.checkUser2faEnabled({ userName })
    const wrap = unwrapLab(result)
    const data =
      wrap.data && typeof wrap.data === 'object'
        ? (wrap.data as Record<string, unknown>)
        : {}
    const enabled = Boolean(data.is2faEnabled)
    try {
      localStorage.setItem(LAB_2FA_KEY, String(enabled))
    } catch {
      /* ignore */
    }
    setPhase(enabled ? 'active' : 'setup')
    return enabled
  }, [userName])

  const loadQr = useCallback(async () => {
    // Prefer JWT actor on API; still send localStorage ids when present.
    if (!userId && !getLabAccessToken()) {
      setError('Not signed in — missing user id.')
      return
    }
    const result = await bookmakerApi.getSecreteKey({
      ...(userId ? { _id: userId } : {}),
      userName,
      name: `${typeof window !== 'undefined' ? window.location.host : 'Bookmaker'} - ${userName || userId}`,
    })
    const wrap = unwrapLab(result)
    if (wrap.value === false) {
      setError(wrap.msg || 'Could not load QR code')
      setAuthImage('')
      return
    }
    setAuthImage(imageFromSecret(wrap.data))
    setError(null)
  }, [userId, userName])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const enabled = await refreshFlag()
        if (cancelled) return
        if (!enabled) await loadQr()
      } catch (ex) {
        if (!cancelled) setError(bmErrorMessage(ex))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refreshFlag, loadQr])

  const onOtpChange = async (raw: string) => {
    const code = raw.replace(/\D/g, '').slice(0, 6)
    setOtp(code)
    setError(null)
    if (code.length !== 6 || (!userId && !getLabAccessToken())) {
      setOtpOk(false)
      return
    }
    setBusy(true)
    try {
      const result = await bookmakerApi.verifySecreteKey({
        token: code,
        ...(userId ? { _id: userId } : {}),
      })
      const wrap = unwrapLab(result)
      if (wrap.value) {
        setOtpOk(true)
        toast.success(wrap.msg || 'Code verified')
      } else {
        setOtpOk(false)
        toast.error(wrap.msg || 'Invalid code')
      }
    } catch (ex) {
      setOtpOk(false)
      setError(bmErrorMessage(ex))
    } finally {
      setBusy(false)
    }
  }

  const setFlag = async (enabled: boolean) => {
    if (!userId && !getLabAccessToken()) return
    setBusy(true)
    setError(null)
    try {
      const result = await bookmakerApi.change2faAuthFlag({
        ...(userId ? { _id: userId } : {}),
        username: userName,
        is2faEnabled: enabled,
      })
      const wrap = unwrapLab(result)
      if (!wrap.value) {
        setError(wrap.msg || 'Could not update 2FA')
        setBusy(false)
        return
      }
      toast.success(wrap.msg || (enabled ? '2FA enabled' : '2FA disabled'))
      if (enabled) {
        // Lab logs out after enable so next login requires OTP.
        clearAllAuth()
        clearLabSession()
        navigate('/login', { replace: true })
        return
      }
      setOtp('')
      setOtpOk(false)
      setPhase('setup')
      await loadQr()
      await refreshFlag()
    } catch (ex) {
      setError(bmErrorMessage(ex))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BmPage
      title="Two-factor Authentication"
      description="Google Authenticator (Lab /googleAuth)"
      error={error}
    >
      {phase === 'setup' ? (
        <Card style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <p style={{ margin: 0 }}>
              Step 1: Download Google Authenticator from the App Store or Google Play.
            </p>
            <p style={{ margin: 0 }}>Step 2: Add an account in Google Authenticator.</p>
            <p style={{ margin: 0 }}>
              Step 3: Scan the QR code and enter the verification code, then enable 2FA.
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 24,
              alignItems: 'flex-start',
            }}
          >
            <div style={{ textAlign: 'center', minWidth: 180 }}>
              {authImage ? (
                <img
                  src={authImage}
                  alt="Google Authenticator QR code"
                  width={180}
                  height={180}
                  style={{ display: 'block', margin: '0 auto', background: '#fff' }}
                />
              ) : (
                <p style={{ opacity: 0.7 }}>Loading QR code…</p>
              )}
            </div>
            <form
              style={{ display: 'grid', gap: 12, flex: 1, minWidth: 220 }}
              onSubmit={(e) => {
                e.preventDefault()
                if (otpOk) void setFlag(true)
              }}
            >
              <Field label="Verification code">
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    name="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otp}
                    invalid={invalid}
                    aria-describedby={describedBy}
                    disabled={busy || !authImage}
                    placeholder="Enter 6-digit code"
                    onChange={(ev) => void onOtpChange(ev.target.value)}
                  />
                )}
              </Field>
              <Button
                type="submit"
                variant="primary"
                busy={busy}
                disabled={busy || !otpOk}
              >
                Enable Two Factor Authentication
              </Button>
            </form>
          </div>
        </Card>
      ) : null}

      {phase === 'active' ? (
        <Card style={{ display: 'grid', gap: 16, maxWidth: 480, textAlign: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Two-factor Authentication is Activated</h2>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setPhase('confirm-disable')
              setOtp('')
              setOtpOk(false)
            }}
          >
            Deactivate
          </Button>
        </Card>
      ) : null}

      {phase === 'confirm-disable' ? (
        <Card style={{ display: 'grid', gap: 16, maxWidth: 420, margin: '0 auto' }}>
          <h2 style={{ margin: 0, fontSize: 18, textAlign: 'center' }}>
            Do you really want to disable Two-factor Authentication?
          </h2>
          <Field label="Verification code">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                name="otp-disable"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                invalid={invalid}
                aria-describedby={describedBy}
                disabled={busy}
                placeholder="Enter 6-digit code"
                onChange={(ev) => void onOtpChange(ev.target.value)}
              />
            )}
          </Field>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Button
              type="button"
              variant="primary"
              busy={busy}
              disabled={busy || !otpOk}
              onClick={() => void setFlag(false)}
            >
              Yes
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setPhase('active')
                setOtp('')
                setOtpOk(false)
              }}
            >
              No
            </Button>
          </div>
        </Card>
      ) : null}
    </BmPage>
  )
}
